import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { getFloatLabel, isFloatListType } from '../../../../utils/filter'
import Button from '../../../Button'
import { CustomSelect } from '../../../CustomSelect'
import {
  FILTER_LIST_ITEM,
  FILTER_LIST_ITEM_PROPS_GETTER,
} from '../../Filter.constants'
import { getFilterOptionLabel } from '../../Filter.utils'
import { getFilterValues } from '../../FilterModalAvailableFilters'
import { getNextFilterOptions } from '../FilterBar.utils'

import { getValue } from './FilterBarMultiSelect.utils'

import * as styles from './FilterBarMultiSelect.module.css'

function FilterBarMultiSelect({
  entity,
  filterOptions,
  getItems,
  isSkeleton,
  item,
  listType,
  onApply,
}) {
  const { t } = useTranslation()

  const [isShowSelected, setShowSelected] = useState(false)
  const [multiFilterOptions, setMultiFilterOptions] = useState(
    filterOptions[item.name] || EMPTY_LIST
  )
  const [prevFilterOption, setPrevFilterOption] = useState(
    filterOptions[item.name]
  )

  // syncs the working copy from filterOptions, during render
  if (filterOptions[item.name] !== prevFilterOption) {
    setPrevFilterOption(filterOptions[item.name])
    setMultiFilterOptions(filterOptions[item.name] || EMPTY_LIST)
  }

  function handleApply(nextFilterOptions) {
    onApply(
      nextFilterOptions || {
        ...filterOptions,
        [item.name]: multiFilterOptions,
      }
    )
  }

  function getSelectedItems(search, params) {
    return getItems(search, {
      ...params,
      items: multiFilterOptions.map(option => option.value),
    })
  }

  const extraItemTextKey = isShowSelected
    ? 'Select.DropdownShowAllBtnKey'
    : 'Select.DropdownShowSelectedBtnKey'

  const apiFunction = isShowSelected ? getSelectedItems : getItems

  function toggleShowSelected() {
    setShowSelected(prev => !prev)
  }

  // Filters with a dedicated `listType` (label, status, role, etc.) have their
  // own rendering branches in `SelectTags`/`SelectList`. For everything else,
  // fall back to the per-filter-name rule from `LABEL_GETTER`.
  function getOptionLabel(option) {
    return getFilterOptionLabel({ entity, name: item.name, option, t })
  }

  const getLabel = isFloatListType(listType)
    ? getFloatLabel
    : listType
      ? null
      : option => getOptionLabel(option) ?? option.label

  const translatedTags = listType
    ? multiFilterOptions
    : multiFilterOptions.map(option => {
        const label = getOptionLabel(option)

        return label === null ? option : { ...option, label }
      })

  function getHandleChangeBaseFilterOptions(params = EMPTY_OBJECT) {
    return value => {
      const { isClear } = params

      const nextFilterOptions = getNextFilterOptions({
        filterOptions: { ...filterOptions, [item.name]: multiFilterOptions },
        isMulti: true,
        key: item.name,
        value,
      })

      setMultiFilterOptions(nextFilterOptions[item.name] || EMPTY_LIST)

      if (isClear || Object.keys(nextFilterOptions).length === 0) {
        handleApply(nextFilterOptions)
      }
    }
  }

  return (
    <CustomSelect
      isMulti
      showBtnTextTooltip
      ExtraItem={Button.Text}
      ListItem={FILTER_LIST_ITEM[item.name]}
      activeItems={getFilterValues(item.name, {
        [item.name]: multiFilterOptions,
      })}
      // TODO: remove when the backend serves the rental_listings single-field alias
      api={item.options ? undefined : apiFunction}
      apiParams={{ field_name: item.single_field_query_param }}
      buttonHeight={32}
      buttonType='white'
      className={styles.tableSelect}
      entity={entity}
      extraItemProps={{
        className: styles.selectButton,
        text: t(extraItemTextKey),
        textClassName: styles.selectButtonText,
        onClick: toggleShowSelected,
      }}
      filterKey={item.query_param}
      getLabel={getLabel}
      getListItemProps={FILTER_LIST_ITEM_PROPS_GETTER[item.name]}
      getValue={getValue}
      inputClassName={styles.input}
      isSkeleton={isSkeleton}
      listType={listType}
      menuClassName={styles.menu}
      options={item.options}
      placeholder={t(item.title)} // changed to item.title cause of chips name and should be reworked https://idwell.atlassian.net/browse/IRD-1499
      searchKey={item.search_param}
      tags={translatedTags}
      onClear={getHandleChangeBaseFilterOptions({ isClear: true })}
      onClick={getHandleChangeBaseFilterOptions()}
      onClose={handleApply}
    />
  )
}

export default FilterBarMultiSelect
